import React from 'react';
import PropTypes from 'prop-types';
import { Formik, Field, FieldArray, Form } from 'formik';
import { FaRegTrashAlt } from 'react-icons/fa';
import {
  Button,
  Input,
  Select,
  Textarea,
  FormControl,
  FormLabel,
  FormErrorMessage,
  VStack,
  SimpleGrid,
  Box,
  HStack,
} from '@chakra-ui/react';
import * as Yup from 'yup';

import {
  GRADE_LEVELS,
  ESSAY_TYPES,
  SECONDARY_ESSAY_TYPES,
  WRITING_CATEGORIES,
  ESSAY_PROMPT_TYPES,
} from 'utils/data/options';

const levelOptions = GRADE_LEVELS;
const essayTypeOptions = ESSAY_TYPES;
const secondaryTypeOptions = SECONDARY_ESSAY_TYPES;
const categoryOptions = WRITING_CATEGORIES;
const essayPromptTypes = ESSAY_PROMPT_TYPES;

const validationSchema = Yup.object({
  level: Yup.string().required('Grade Level is required'),
  primaryType: Yup.string().required('Essay Type is required'),
  category: Yup.string().required('Essay Category is required'),
  shortPrompt: Yup.string().required('Short Prompt is required'),
  prompt: Yup.string()
    .required('Prompt is required')
    .max(800, 'Max 800 characters'),
});

const EssayPromptForm = ({ data, updatePrompt, isEdit, isDisabled }) => {
  // console.log('EssayPromptForm, data: ', data);
  return (
    <Formik
      initialValues={data}
      enableReinitialize
      validationSchema={validationSchema}
    // onSubmit={(values) => {
    //   console.log('update prompt values: ', values);
    //   updatePrompt(values);
    // }}
    >
      {({ values, errors, touched }) => (
        <Form>
          <VStack spacing={6} align="stretch">
            {/* Essay Title */}
            <FormControl>
              <FormLabel htmlFor="promptTitle">
                Essay Title (optional)
              </FormLabel>
              <Field
                as={Input}
                id="promptTitle"
                name="promptTitle"
                placeholder="Essay Title"
              />
            </FormControl>

            {/* Grade Level, Essay Type, Minimal Word Count */}
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
              <FormControl isInvalid={errors.level && touched.level}>
                <FormLabel htmlFor="level">Grade Level</FormLabel>
                <Field
                  as={Select}
                  id="level"
                  name="level"
                  placeholder="Select Grade Level"
                >
                  {levelOptions.map((level) => (
                    <option key={level.id} value={level.id}>
                      {level.name}
                    </option>
                  ))}
                </Field>
                <FormErrorMessage>{errors.level}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={errors.category && touched.category}>
                <FormLabel htmlFor="category">Writing Category</FormLabel>
                <Field
                  as={Select}
                  id="category"
                  name="category"
                  placeholder="Select Writing Category"
                >
                  {categoryOptions.map((cat) => (
                    <option key={cat.id} value={cat.name.toLocaleLowerCase()}>
                      {cat.name}
                    </option>
                  ))}
                </Field>
                <FormErrorMessage>{errors.category}</FormErrorMessage>
              </FormControl>
              <FormControl
                isInvalid={errors.primaryType && touched.primaryType}
              >
                <FormLabel htmlFor="primaryType">Essay Type</FormLabel>
                <Field
                  as={Select}
                  id="primaryType"
                  name="primaryType"
                  placeholder="Select Essay Type"
                >
                  {essayTypeOptions.map((essayType) => (
                    <option
                      key={essayType.id}
                      value={essayType.name.toLocaleLowerCase()}
                    >
                      {essayType.name}
                    </option>
                  ))}
                </Field>
                <FormErrorMessage>{errors.primaryType}</FormErrorMessage>
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="secondaryType">
                  Secondary Essay Type (optional)
                </FormLabel>
                <Field
                  as={Select}
                  id="secondaryType"
                  name="secondaryType"
                  placeholder="Select Essay Type"
                >
                  {secondaryTypeOptions.map((essayType) => (
                    <option
                      key={essayType.id}
                      value={essayType.name.toLocaleLowerCase()}
                    >
                      {essayType.name}
                    </option>
                  ))}
                </Field>
              </FormControl>
              <FormControl>
                <FormLabel htmlFor="promptType">
                  Prompt Type (optional)
                </FormLabel>
                <Field
                  as={Select}
                  id="promptType"
                  name="promptType"
                  placeholder="Select Prompt Type"
                >
                  {essayPromptTypes.map((promptType) => (
                    <option key={promptType.id} value={promptType.name}>
                      {promptType.name}
                    </option>
                  ))}
                </Field>
              </FormControl>
            </SimpleGrid>

            {/* Short Prompt */}
            <FormControl isInvalid={errors.shortPrompt && touched.shortPrompt}>
              <FormLabel htmlFor="shortPrompt">Short Prompt</FormLabel>
              <Field
                as={Input}
                id="shortPrompt"
                name="shortPrompt"
                placeholder="Short Prompt"
              />
              <FormErrorMessage>{errors.shortPrompt}</FormErrorMessage>
            </FormControl>

            {/* Prompt */}
            <FormControl isInvalid={errors.prompt && touched.prompt}>
              <FormLabel htmlFor="prompt">
                Prompt (Max 800 characters)
              </FormLabel>
              <Field
                as={Textarea}
                id="prompt"
                name="prompt"
                placeholder="Enter the essay prompt"
                maxLength={800}
              />
              <FormErrorMessage>{errors.prompt}</FormErrorMessage>
            </FormControl>

            {/* illustration */}
            <FormControl>
              <FormLabel htmlFor="illustration">
                Illustration (optional)
              </FormLabel>
              <Field
                as={Input}
                id="illustration"
                name="illustration"
                placeholder="Illustration url"
              />
            </FormControl>

            {/* thumbnail image */}
            <FormControl>
              <FormLabel htmlFor="thumbnailUrl">
                Thumbnail Image (optional)
              </FormLabel>
              <Field
                as={Input}
                id="thumbnailUrl"
                name="thumbnailUrl"
                placeholder="thumbnail image url"
              />
            </FormControl>

            {/* Types and Tags in two columns */}
            <SimpleGrid columns={[1, null, 2]} spacing={6}>
              <FormControl>
                <FormLabel htmlFor="minWordCount">
                  Minimal Word Count (optional)
                </FormLabel>
                <Field
                  as={Input}
                  type="number"
                  id="minWordCount"
                  name="minWordCount"
                  placeholder="0"
                />
              </FormControl>
              <FieldArray name="tags">
                {({ remove, push }) => (
                  <Box>
                    <FormLabel>Tags (optional)</FormLabel>
                    {values?.tags?.map((tag, index) => (
                      <HStack key={index} spacing={4} align="center" mb={2}>
                        <FormControl>
                          <Field
                            as={Input}
                            name={`tags.${index}`}
                            placeholder={`Tag ${index + 1}`}
                          />
                        </FormControl>
                        <FaRegTrashAlt
                          color="red"
                          onClick={() => remove(index)}
                        />
                      </HStack>
                    ))}
                    <Button
                      mt={2}
                      colorScheme="teal"
                      size="sm"
                      onClick={() => push('')}
                    >
                      Add Tag
                    </Button>
                  </Box>
                )}
              </FieldArray>
            </SimpleGrid>

            {/* Submit Button */}
            {isEdit ? (
              <Box textAlign="center">
                <Button
                  isDisabled={isDisabled}
                  isLoading={isDisabled}
                  variant="outline"
                  colorScheme="green"
                  size="md"
                  mr={4}
                  mb={8}
                  onClick={() => updatePrompt(values, 1)}
                >
                  Save
                </Button>
                <Button
                  isDisabled={isDisabled}
                  isLoading={isDisabled}
                  colorScheme="green"
                  size="md"
                  ml={4}
                  mb={8}
                  onClick={() => updatePrompt(values, 2)}
                >
                  Submit
                </Button>
              </Box>
            ) : (
              <Box textAlign="center" mt={4}>
                <Button
                  isDisabled={isDisabled}
                  isLoading={isDisabled}
                  colorScheme="green"
                  size="md"
                  mb={8}
                  onClick={() => updatePrompt(values, 1)}
                >
                  Create
                </Button>
              </Box>
            )}
          </VStack>
        </Form>
      )}
    </Formik>
  );
};

EssayPromptForm.propTypes = {
  updatePrompt: PropTypes.func.isRequired,
  isEdit: PropTypes.bool.isRequired,
  isDisabled: PropTypes.bool.isRequired,
  data: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.arrayOf(PropTypes.string),
    ])
  ),
};

export default EssayPromptForm;
